import React from 'react'
import { capitalizeFirstLetter, formatDate } from '@btime/helpers'

import { Table, TableCol, TableRow } from '../../../../components/pdf/Table'
import { useServiceOrder } from '../_load/ServiceOrderLoader'
import Separator from '../../../../components/pdf/Separator'
import Image from '../../../../components/pdf/Image'
import View from '../../../../components/pdf/View'
import Text from '../../../../components/pdf/Text'
import Grid, { Col } from '../../../../components/pdf/Grid'
import {
  filterRepliesByParentReference,
  findReplyByQuestionId,
  getComplement,
  getRepliesFromBlockQuestion,
  getReplyValue,
  toCurrency
} from '../_helpers/pdfHelpers'
import Icon from '../../../../components/shapes/Icon'

const InternationalTemplate = () => {
  const parentServiceOrder = useServiceOrder()
  const replies = parentServiceOrder.replies
  const placeName = parentServiceOrder.place?.name
  const date = getReplyValue(findReplyByQuestionId(replies, 478))
  const month = formatDate('MMMM', date)
  const year = formatDate('YYYY', date)
  const userId = parentServiceOrder?.user?.id

  const paymentMethod = getReplyValue(findReplyByQuestionId(replies, 492))
  const generalObservation = getReplyValue(findReplyByQuestionId(replies, 527))
  const others = getReplyValue(findReplyByQuestionId(replies, 528))
  const optional = getReplyValue(findReplyByQuestionId(replies, 529))
  const generalImages = getReplyValue(findReplyByQuestionId(replies, 652)) || []
  const generalImagesList = generalImages.map(image => image.src)

  const userSignature = {
    5: '/pdfs/universal/assinatura-patricia.jpg',
    6: '/pdfs/universal/assinatura-claudia.jpg',
    7: '/pdfs/universal/assinatura-helena.jpg'
  }

  const airFareBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 480)
    const airFareReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const airFareResult = {}
    airFareReplies.forEach(airFareReply => {
      const blockIndex = airFareReply.blockIndex
      let airFareData = airFareResult[blockIndex]
      if (!airFareData) {
        const blockReplies = getRepliesFromBlockQuestion(airFareReplies, blockIndex)
        airFareData = {
          from: getReplyValue(findReplyByQuestionId(blockReplies, 481)),
          to: getReplyValue(findReplyByQuestionId(blockReplies, 482)),
          departureDate: formatDate('DD/MM/YYYY HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 510))),
          arrivalDate: formatDate('DD/MM/YYYY HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 511))),
          airCompany: getReplyValue(findReplyByQuestionId(blockReplies, 485)),
          baggage: getReplyValue(findReplyByQuestionId(blockReplies, 486))
        }
        airFareResult[blockIndex] = airFareData
      }
    })
    return Object.values(airFareResult)
  }

  const TaxBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 488)
    const TaxReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const taxResult = {}
    TaxReplies.forEach(taxReply => {
      const blockIndex = taxReply.blockIndex
      let taxData = taxResult[blockIndex]
      if (!taxData) {
        const blockReplies = getRepliesFromBlockQuestion(TaxReplies, blockIndex)
        taxData = {
          tax: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 643))),
          boardingTax: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 644))),
          total: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 645)))
        }
        taxResult[blockIndex] = taxData
      }
    })
    return Object.values(taxResult)
  }

  const ticketBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 499)
    const ticketReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const ticketResult = {}
    ticketReplies.forEach(ticketReply => {
      const blockIndex = ticketReply.blockIndex
      let ticketData = ticketResult[blockIndex]
      if (!ticketData) {
        const blockReplies = getRepliesFromBlockQuestion(ticketReplies, blockIndex)
        ticketData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 500)),
          price: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 501)))
        }
        ticketResult[blockIndex] = ticketData
      }
    })
    return Object.values(ticketResult)
  }

  const accommodationBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 515)
    const accommodationReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const accommodationResult = {}
    accommodationReplies.forEach(accommodationReply => {
      const blockIndex = accommodationReply.blockIndex
      let accommodationData = accommodationResult[blockIndex]
      if (!accommodationData) {
        const blockReplies = getRepliesFromBlockQuestion(accommodationReplies, blockIndex)
        const images = getReplyValue(findReplyByQuestionId(blockReplies, 651)) || []
        const hotelImages = images.map(image => image.src)
        accommodationData = {
          hotelName: getReplyValue(findReplyByQuestionId(blockReplies, 493)),
          hotelAddress: getReplyValue(findReplyByQuestionId(blockReplies, 494)),
          hotelCategory: getReplyValue(findReplyByQuestionId(blockReplies, 495))?.name,
          hotelAlimentation: getReplyValue(findReplyByQuestionId(blockReplies, 496))
            .map(item => item.name)
            .join(', '),
          hotelPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 497))),
          hotelObservation: getReplyValue(findReplyByQuestionId(blockReplies, 498)),
          hotelImages
        }
        accommodationResult[blockIndex] = accommodationData
      }
    })
    return Object.values(accommodationResult)
  }

  const carRentBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 516)
    const carRentReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const carRentResult = {}
    carRentReplies.forEach(carRentReply => {
      const blockIndex = carRentReply.blockIndex
      let carRentData = carRentResult[blockIndex]
      if (!carRentData) {
        const blockReplies = getRepliesFromBlockQuestion(carRentReplies, blockIndex)
        carRentData = {
          carRentStartDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 502))),
          carRentEndDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 503))),
          carRentModel: getReplyValue(findReplyByQuestionId(blockReplies, 504)),
          carRentPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 505)))
        }
        carRentResult[blockIndex] = carRentData
      }
    })
    return Object.values(carRentResult)
  }

  const trainBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 517)
    const trainReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const trainResult = {}
    trainReplies.forEach(trainReply => {
      const blockIndex = trainReply.blockIndex
      let trainData = trainResult[blockIndex]
      if (!trainData) {
        const blockReplies = getRepliesFromBlockQuestion(trainReplies, blockIndex)
        trainData = {
          trainStartPlace: getReplyValue(findReplyByQuestionId(blockReplies, 506)),
          trainEndPlace: getReplyValue(findReplyByQuestionId(blockReplies, 507)),
          trainStartDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 508))),
          trainStartTime: formatDate('HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 508))),
          trainPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 509)))
        }
        trainResult[blockIndex] = trainData
      }
    })
    return Object.values(trainResult)
  }

  const transferBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 518)
    const transferReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const transferResult = {}
    transferReplies.forEach(trainReply => {
      const blockIndex = trainReply.blockIndex
      let transferData = transferResult[blockIndex]
      if (!transferData) {
        const blockReplies = getRepliesFromBlockQuestion(transferReplies, blockIndex)
        transferData = {
          transferStartPlace: getReplyValue(findReplyByQuestionId(blockReplies, 519)),
          transferEndPlace: getReplyValue(findReplyByQuestionId(blockReplies, 520)),
          transferStartDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 521))),
          transferStartTime: formatDate('HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 521))),
          transferPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 522)))
        }
        transferResult[blockIndex] = transferData
      }
    })
    return Object.values(transferResult)
  }

  const maritimeCruiseBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 534)
    const maritimeCruiseReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const maritimeCruiseResult = {}
    maritimeCruiseReplies.forEach(maritimeCruiseReply => {
      const blockIndex = maritimeCruiseReply.blockIndex
      let maritimeCruiseData = maritimeCruiseResult[blockIndex]
      if (!maritimeCruiseData) {
        const blockReplies = getRepliesFromBlockQuestion(maritimeCruiseReplies, blockIndex)
        maritimeCruiseData = {
          maritimeCruiseStartPlace: getReplyValue(findReplyByQuestionId(blockReplies, 523)),
          maritimeCruiseEndPlace: getReplyValue(findReplyByQuestionId(blockReplies, 524)),
          maritimeCruiseStartDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 525))),
          maritimeCruiseStartTime: formatDate('HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 525))),
          maritimeCruisePrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 526)))
        }
        maritimeCruiseResult[blockIndex] = maritimeCruiseData
      }
    })
    return Object.values(maritimeCruiseResult)
  }

  const travelInsuranceBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 530)
    const travelInsuranceReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const travelInsuranceResult = {}
    travelInsuranceReplies.forEach(travelInsuranceReply => {
      const blockIndex = travelInsuranceReply.blockIndex
      let travelInsuranceData = travelInsuranceResult[blockIndex]
      if (!travelInsuranceData) {
        const blockReplies = getRepliesFromBlockQuestion(travelInsuranceReplies, blockIndex)
        travelInsuranceData = {
          travelInsuranceUser: getReplyValue(findReplyByQuestionId(blockReplies, 531)),
          travelInsuranceUserIncludes: getReplyValue(findReplyByQuestionId(blockReplies, 532)),
          travelInsurancePrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 533)))
        }
        travelInsuranceResult[blockIndex] = travelInsuranceData
      }
    })
    return Object.values(travelInsuranceResult)
  }

  const airFareReplies = airFareBlock()
  const taxReplies = TaxBlock()
  const ticketReplies = ticketBlock()
  const accommodationReplies = accommodationBlock()
  const carRentReplies = carRentBlock()
  const trainReplies = trainBlock()
  const transferReplies = transferBlock()
  const maritimeCruiseReplies = maritimeCruiseBlock()
  const travelInsuranceReplies = travelInsuranceBlock()

  return (
    <View pageBreakAfter>
      <Grid justify="center" align="center">
        <Image src="/pdfs/universal/internacional-header.jpg" width="100%" />
      </Grid>
      <Separator weight={10} />

      <Grid justify="center">
        <Text size={32} bold>
          {placeName} - {capitalizeFirstLetter(month)} {year}
        </Text>
      </Grid>

      <Separator weight={10} />

      {!!airFareReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/passagens-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a" style={{ borderTop: '1px solid #fff' }}>
            <TableRow color="#0a374a">
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#0a374a">
                <Text color="#ffffff" style={{ fontWeight: 700 }}>
                  De
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text color="#ffffff" style={{ fontWeight: 700 }}>
                  Para
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Data e Hora saída
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Data e Hora chegada
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Bagagem
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Cia Aérea
                </Text>
              </TableCol>
            </TableRow>
            {airFareReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.from}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.to}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.departureDate}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.arrivalDate}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.baggage ? 'Sim' : 'Não'}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.airCompany}</Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={5} />
        </>
      )}

      {!!taxReplies.length && (
        <>
          <Table width="100%" color="#0a374a">
            <TableRow color="#0a374a">
              <TableCol width="33%" bgColor="#0a374a" style={{ padding: '10px' }} color="#0a374a">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor da Tarifa
                </Text>
              </TableCol>
              <TableCol width="33%" bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor da Taxa de Embarque
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor Total
                </Text>
              </TableCol>
            </TableRow>
            {taxReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.tax}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.boardingTax}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.total}</Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          {paymentMethod && (
            <>
              <Separator weight={5} />
              <Table width="100%" color="#0a374a">
                <TableRow color="#0a374a">
                  <TableCol width="45%" bgColor="#0a374a" style={{ padding: '10px' }}>
                    <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                      Forma de pagamento:
                    </Text>
                  </TableCol>
                  <TableCol style={{ padding: '10px' }}>
                    <Text>{paymentMethod}</Text>
                  </TableCol>
                </TableRow>
              </Table>
            </>
          )}

          <Separator weight={10} />
        </>
      )}

      {!!accommodationReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/acomodacoes-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a" style={{ borderTop: '1px solid #fff' }}>
            <TableRow color="#0a374a">
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#0a374a">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Nome do Hotel
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Categoria
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Alimentação
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor
                </Text>
              </TableCol>
              <TableCol bgColor="#0a374a" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Observação
                </Text>
              </TableCol>
            </TableRow>
            {accommodationReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>🏨 {reply.hotelName}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.hotelCategory}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.hotelAlimentation}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.hotelPrice}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>{reply.hotelObservation}</Text>
                    </TableCol>
                  </TableRow>
                  <TableRow color="#0a374a">
                    <TableCol bgColor="#0a374a" style={{ padding: '10px' }}>
                      <Text color="#ffffff" style={{ fontWeight: 700 }}>
                        Endereço do Hotel
                      </Text>
                    </TableCol>
                    <TableCol colSpan={4} style={{ padding: '10px' }} color="#0a374a">
                      <a
                        href={`https://maps.google.com/maps?q=${encodeURIComponent(reply.hotelAddress.search)}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          textDecoration: 'none',
                          width: 'fit-content'
                        }}
                      >
                        <Text>{`📍 ${reply.hotelAddress.search} ${getComplement(
                          reply.hotelAddress?.complement
                        )}`}</Text>
                        <Icon name="BsLink45Deg" size="1rem" color="#000000" />
                      </a>
                    </TableCol>
                  </TableRow>
                  {!!reply.hotelImages.length && (
                    <>
                      <TableRow color="#0a374a">
                        <TableCol
                          colSpan={5}
                          bgColor="#0a374a"
                          style={{ padding: '10px', borderTop: '1px solid #fff' }}
                        >
                          <Text color="#ffffff" style={{ fontWeight: 700 }} align="center">
                            Fotos do Hotel
                          </Text>
                        </TableCol>
                      </TableRow>
                      <TableRow color="#0a374a">
                        <TableCol colSpan={5} style={{ padding: '10px' }} color="#0a374a">
                          <Grid direction="row" wrap>
                            {reply.hotelImages?.map((image, index) => {
                              return (
                                <Col key={index} width="50%" gutter={5}>
                                  <Image src={image} width="100%" />
                                </Col>
                              )
                            })}
                          </Grid>
                        </TableCol>
                      </TableRow>
                    </>
                  )}
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!ticketReplies.length && (
        <>
          <Grid>
            <Image src="/pdfs/universal/tickets-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            {ticketReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>
                        🎫 {reply.name} - {reply.price} por pessoa
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!carRentReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/aluguel-carro-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            {carRentReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>
                        🚘 Aluguel de carro {reply.carRentModel} de {reply.carRentStartDate} a {reply.carRentEndDate} -{' '}
                        {reply.carRentPrice}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!trainReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/trens-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            {trainReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>
                        🚆 Trem {reply.trainStartPlace} a {reply.trainEndPlace} dia {reply.trainStartDate} -{' '}
                        {reply.trainPrice} - Saída às {reply.trainStartTime}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!transferReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/traslados-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            {transferReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>
                        🚌 Transfer {reply.transferStartPlace} a {reply.transferEndPlace} dia {reply.transferStartDate}{' '}
                        - {reply.transferPrice} - Saída às {reply.transferStartTime}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!maritimeCruiseReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/cruzeiro-header.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            {maritimeCruiseReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>
                        🛳️ Cruzeiro {reply.maritimeCruiseStartPlace} a {reply.maritimeCruiseEndPlace} dia{' '}
                        {reply.maritimeCruiseStartDate} - {reply.maritimeCruisePrice} - Saída às{' '}
                        {reply.maritimeCruiseStartTime}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!travelInsuranceReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/seguro-viagem-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            {travelInsuranceReplies.map(reply => {
              return (
                <>
                  <TableRow color="#0a374a">
                    <TableCol style={{ padding: '10px' }} color="#0a374a">
                      <Text>
                        🩺 Seguro Viagem {reply.travelInsuranceUser} - {reply.travelInsuranceUserIncludes} -{' '}
                        {reply.travelInsurancePrice}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!generalObservation && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/observacoes-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            <TableRow color="#0a374a">
              <TableCol style={{ padding: '10px' }} color="#0a374a">
                <Text>📝 {generalObservation}</Text>
              </TableCol>
            </TableRow>
            {!!generalImagesList.length && (
              <>
                <TableRow color="#0a374a">
                  <TableCol style={{ padding: '10px' }} bgColor="#0a374a">
                    <Text color="#ffffff" style={{ fontWeight: 700 }} align="center">
                      Fotos gerais
                    </Text>
                  </TableCol>
                </TableRow>
                <TableRow color="#0a374a">
                  <TableCol colSpan={5} style={{ padding: '10px' }} color="#0a374a">
                    <Grid direction="row" wrap>
                      {generalImagesList.map((image, index) => {
                        return (
                          <Col key={index} width="50%" gutter={5}>
                            <Image src={image} width="100%" />
                          </Col>
                        )
                      })}
                    </Grid>
                  </TableCol>
                </TableRow>
              </>
            )}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!others && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/outros-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#0a374a">
            <TableRow color="#0a374a">
              <TableCol style={{ padding: '10px' }} color="#0a374a">
                <Text>📌 {others}</Text>
              </TableCol>
            </TableRow>
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!optional && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/opcionais-banner.jpg" width="100%" />
          </Grid>

          <Table width="100%" color="#0a374a">
            <TableRow color="#0a374a">
              <TableCol style={{ padding: '10px' }} color="#0a374a">
                <Text>✅ {optional}</Text>
              </TableCol>
            </TableRow>
          </Table>
          <Separator weight={10} />
        </>
      )}

      <Grid justify="center" align="center">
        <Image src="/pdfs/universal/information.jpg" width="100%" />
      </Grid>

      <Separator weight={10} />

      <Grid justify="center" align="center">
        <Image src={userSignature[userId]} width={400} />
      </Grid>
    </View>
  )
}

export default InternationalTemplate
